import { createFileRoute } from "@tanstack/react-router"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb, CtaBand, SimpleTable } from "../components/landing"
import {
  PROCESSORS,
  TRUST_CHECKED,
  TRUST_FACTS,
  type TrustFact,
} from "../content/pages/trust-facts"

export const trustSeo = () =>
  buildHead({
    title: "Security, privacy and where your data lives",
    description:
      "Where Kestly stores your records, what we collect, who processes it, how long we keep it, and how to export or delete it.",
    path: "/trust",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Security and privacy" }])],
  })

export const Route = createFileRoute("/_brand-layout/trust")({
  head: () => {
    const { meta, links, scripts } = trustSeo()
    return { meta, links, scripts }
  },
  component: TrustPage,
})

const sourceCell = ({ source }: TrustFact) => (
  <span className="text-muted-foreground">
    {source.href ? (
      <a
        href={source.href}
        className="hover:text-foreground focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
      >
        {source.label}
      </a>
    ) : (
      source.label
    )}
    , checked {TRUST_CHECKED}
  </span>
)

const rows = (facts: readonly TrustFact[]) =>
  facts.map((f) => ({ header: f.label, cells: [f.value, sourceCell(f)] }))

export function TrustPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Security and privacy" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
          How we look after your records
        </h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          What we store, where, who else touches it, and how you can take it with you or delete it.
          Read the full{" "}
          <a
            href="/legal/privacy"
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            privacy policy
          </a>{" "}
          too.
        </p>
      </header>

      <section className="px-band grid gap-4 py-8">
        <h2 className="text-2xl font-semibold">Where your data lives</h2>
        <SimpleTable
          caption="Where your data lives and how long it is kept"
          columns={["Topic", "What we do", "Source"]}
          rows={rows(TRUST_FACTS)}
        />
      </section>

      <section className="px-band grid gap-4 py-8">
        <h2 className="text-2xl font-semibold">Who else processes data</h2>
        <SimpleTable
          caption="Who else processes data"
          columns={["Service", "What it does", "Source"]}
          rows={rows(PROCESSORS)}
        />
        <p className="text-muted-foreground text-sm">
          Questions about your data? Email{" "}
          <a
            href={`mailto:${config.core.supportEmail}`}
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            {config.core.supportEmail}
          </a>
          .
        </p>
      </section>

      <CtaBand
        title="Try it with your own trips"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
